/**
 * Resolve a CSS color specification (var(), light-dark(), color-mix(),
 * relative color syntax, etc.) into a plain `rgb()`/`rgba()` string by
 * letting the browser's CSS engine evaluate it on a real element.
 *
 * Why this exists: canvas-rendered consumers (ECharts/zrender) parse
 * colors with their own pipeline. Safari's canvas color parser doesn't
 * resolve modern Color Level 4/5 functions like `light-dark()` or
 * `hsl(from <color> ...)`, so feeding those strings directly leaves
 * axis lines and labels unrendered. A hidden `<span>` whose `color`
 * property holds the spec forces the CSS engine to compute it to RGB,
 * which canvas handles in every browser.
 *
 * Lifecycle: create the probe once per chart, call `resolve()` from
 * `buildOptions()` so theme changes are picked up on each setOption,
 * and `dispose()` from the chart's cleanup.
 */
export type CssColorProbe = {
	resolve: (spec: string, fallback: string) => string;
	dispose: () => void;
};

export function createCssColorProbe(): CssColorProbe {
	if (typeof document === "undefined") {
		return {
			resolve: (_spec, fallback) => fallback,
			dispose: () => {},
		};
	}

	const el = document.createElement("span");
	el.style.position = "absolute";
	el.style.visibility = "hidden";
	el.style.pointerEvents = "none";
	el.setAttribute("aria-hidden", "true");
	document.body.appendChild(el);

	return {
		resolve: (spec, fallback) => {
			// Reset first so an invalid spec doesn't inherit the prior value.
			el.style.color = "";
			el.style.color = spec;
			// Browser rejected the spec entirely (style.color stays "").
			if (!el.style.color) return fallback;
			return window.getComputedStyle(el).color || fallback;
		},
		dispose: () => el.remove(),
	};
}
